import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Song, Player, Guess } from '../types';
import * as socketService from '../services/socket';
import SongSearch from '../components/SongSearch';
import SongPlayer from '../components/SongPlayer';
import PlayerList from '../components/PlayerList';
import GuessChat from '../components/GuessChat';
import PreloadingScreen from '../components/PreloadingScreen';
import BlankSongTitle from '../components/BlankSongTitle';
import ThemePicker from '../components/ThemePicker';
import { toast } from 'react-hot-toast';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { getAvatarHue, getInitial } from '../utils/avatar';
import { copyPlainText, isTextEditableElement } from '../utils/dom';

interface LocationState {
  username?: string;
  createRoom?: boolean;
  devBots?: number;
}

const Room: React.FC = () => {
  const { roomId: roomIdParam } = useParams<{ roomId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isDevMode = process.env.NODE_ENV === 'development';
  
  // Initial state setup
  const locationState = location.state as LocationState | undefined;
  const initialUsername = locationState?.username || '';
  const shouldCreateRoom = Boolean(locationState?.createRoom);
  
  const [roomId, setRoomId] = useState<string>(socketService.normalizeRoomId(roomIdParam || ''));
  const [username, setUsername] = useState<string>(initialUsername);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [songSelectionStarted, setSongSelectionStarted] = useState(false);
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);
  const [currentSongUrl, setCurrentSongUrl] = useState('');
  const [currentSongTitle, setCurrentSongTitle] = useState('');
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [guesses, setGuesses] = useState<Guess[]>([]);
  const [hasGuessedCorrectly, setHasGuessedCorrectly] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [scores, setScores] = useState<Player[]>([]);
  const [revealedSong, setRevealedSong] = useState<Song | null>(null);
  const [isCreator, setIsCreator] = useState(false);
  const [theme, setTheme] = useState<string>('');
  const [gameTheme, setGameTheme] = useState<string>('');
  const [closeGuesses, setCloseGuesses] = useState<Guess[]>([]);
  
  // Preloading
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadedCount, setPreloadedCount] = useState(0);
  const [totalSongs, setTotalSongs] = useState(0);
  
  const [playersWhoSelected, setPlayersWhoSelected] = useState<string[]>([]);
  const [isCurrentSongSelector, setIsCurrentSongSelector] = useState(false);

  // Song selection download UX (only the selecting player sees a blocking spinner)
  const [isConfirmingSelection, setIsConfirmingSelection] = useState<boolean>(false);
  // Server-side rejection of the last pick (too long, download failed, ...) shown inline in SongSearch
  const [selectionError, setSelectionError] = useState<string>('');
  
  // Theme vote states
  const [themeVotes, setThemeVotes] = useState<number>(0);
  const [requiredThemeVotes, setRequiredThemeVotes] = useState<number>(0);
  const [hasVotedInvalidTheme, setHasVotedInvalidTheme] = useState(false);
  const [themeVotePassed, setThemeVotePassed] = useState(false);
  const [currentSongSelector, setCurrentSongSelector] = useState<{id: string, username: string} | null>(null);
  
  // Connection state
  const [reconnecting, setReconnecting] = useState<boolean>(false);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const [hasJoined, setHasJoined] = useState<boolean>(false);
  const [devBotsAdded, setDevBotsAdded] = useState<boolean>(false);

  /**
   * Our stable player id. This is what the server keys players by (not the socket id), so it
   * stays valid across refreshes, back/forward navigation and reconnects. Kept in a ref as well
   * so socket handlers always read the latest value.
   */
  const [selfId, setSelfId] = useState<string>(() => socketService.getPlayerId());
  const selfIdRef = useRef<string>(selfId);
  const gameThemeRef = useRef<string>('');
  const devBotsAddedRef = useRef<boolean>(false);
  const roomIdRef = useRef<string>(roomId);
  const usernameRef = useRef<string>(username);

  useEffect(() => {
    gameThemeRef.current = gameTheme;
  }, [gameTheme]);

  useEffect(() => {
    roomIdRef.current = roomId;
    usernameRef.current = username;
  }, [roomId, username]);

  useEffect(() => {
    devBotsAddedRef.current = devBotsAdded;
  }, [devBotsAdded]);
  
  useEffect(() => {
    if (hasGuessedCorrectly) {
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#8A2BE2', '#4CAF50', '#FFFFFF', '#FFD700']
      });
    }
  }, [hasGuessedCorrectly]);

  /**
   * Backspace outside a text field is the #1 way players accidentally leave the game: some
   * browsers/extensions still map it to "history back", which tears down the connection.
   * Swallow it whenever the focus is not in an editable field. (GuessChat separately refocuses
   * the guess box when you start typing, so the keystroke isn't lost either.)
   */
  useEffect(() => {
    const preventBackspaceNavigation = (event: KeyboardEvent) => {
      if (event.key !== 'Backspace') return;
      if (isTextEditableElement(event.target)) return;
      event.preventDefault();
    };
    window.addEventListener('keydown', preventBackspaceNavigation);
    return () => window.removeEventListener('keydown', preventBackspaceNavigation);
  }, []);

  useEffect(() => {
    const normalizedRoomId = socketService.normalizeRoomId(roomIdParam || '');

    // Set room ID from URL parameter
    setRoomId(normalizedRoomId);

    if (roomIdParam && normalizedRoomId && roomIdParam !== normalizedRoomId) {
      navigate(`/room/${normalizedRoomId}`, { replace: true, state: locationState });
    }
    
    // Check if we have a username
    const checkUsername = async () => {
      // If we already have a username from location state, use it
      if (username) {
        return;
      }
      
      // Try to get username from URL parameters
      const params = new URLSearchParams(location.search);
      const paramUsername = params.get('username');
      if (paramUsername) {
        setUsername(paramUsername);
        return;
      }
      
      // Try to get username from session storage
      const { username: storedUsername } = socketService.getSessionInfo();
      if (storedUsername) {
        setUsername(storedUsername);
        return;
      }
      
      // If no username is found, send them to join with this room pre-filled
      navigate(normalizedRoomId ? socketService.getInvitePath(normalizedRoomId) : '/join');
    };
    
    checkUsername();
  }, [roomIdParam, location, navigate, username, locationState]);

  /** Apply the authoritative room snapshot the server sends on (re)join and on request. */
  const applyRoomState = useCallback((data: socketService.RoomStatePayload) => {
    if (!data || !data.players) return;

    if (data.selfId) {
      selfIdRef.current = data.selfId;
      setSelfId(data.selfId);
    }
    const myId = selfIdRef.current;

    if (data.roomId && data.roomId !== roomIdRef.current) {
      setRoomId(data.roomId);
    }

    setPlayers(data.players);
    setHasJoined(true);

    const me = data.players.find((p: Player) => p.id === myId);
    setIsCreator(Boolean(me?.isCreator));
    if (me && me.username !== usernameRef.current) {
      // The server keeps the original name for a seat that is being reclaimed mid-game.
      setUsername(me.username);
      socketService.storeSessionInfo(data.roomId, me.username);
    }

    if (!devBotsAddedRef.current && data.players.some((p: Player) => p.isBot)) {
      setDevBotsAdded(true);
    }

    setGameTheme(data.theme || '');
    setPlayersWhoSelected(data.players.filter((p: Player) => p.hasSelectedSong).map((p: Player) => p.id));
    setSelectedSong(data.selfSelectedSong ?? null);
    setIsConfirmingSelection(false);
    if (typeof data.totalRounds === 'number') setTotalRounds(data.totalRounds);

    const applyRoundInfo = () => {
      const info = data.currentRoundInfo;
      if (!info) {
        setCurrentSongUrl('');
        setCurrentSongTitle('');
        setCurrentSongSelector(null);
        setIsCurrentSongSelector(false);
        setRevealedSong(null);
        return;
      }
      setCurrentRound(info.round);
      setTotalRounds(info.totalRounds);
      setCurrentSongUrl(info.songUrl);
      setCurrentSongTitle(info.songTitle);
      setRoundStartTime(info.roundStartTime);
      setRevealedSong(null);
      setCurrentSongSelector(info.songSelector);
      setIsCurrentSongSelector(Boolean(info.songSelector && info.songSelector.id === myId));
      setHasGuessedCorrectly(Boolean(data.selfHasGuessedCorrectly));
      setThemeVotes(info.themeVote.currentVotes);
      setRequiredThemeVotes(info.themeVote.requiredVotes);
      setThemeVotePassed(info.themeVote.passed);
      setHasVotedInvalidTheme(Boolean(data.selfHasVotedInvalidTheme));
      setCloseGuesses([]);
      // Rebuild the "solved" panel from the guesses made before we (re)joined.
      setGuesses(
        (info.correctGuesses || []).map((g, index) => ({
          playerId: g.playerId,
          username: g.username,
          guess: '',
          isCorrect: true,
          score: g.score,
          position: index + 1
        }))
      );
    };

    switch (data.gameState) {
      case 'song_selection':
        setSongSelectionStarted(true);
        setGameStarted(false);
        setIsPreloading(false);
        setGameEnded(false);
        break;
      case 'preloading':
        setSongSelectionStarted(true);
        setIsPreloading(true);
        setGameStarted(false);
        setGameEnded(false);
        if (data.preloadProgress) {
          setPreloadedCount(data.preloadProgress.preloaded);
          setTotalSongs(data.preloadProgress.total);
        }
        break;
      case 'playing':
        setSongSelectionStarted(true);
        setIsPreloading(false);
        setGameStarted(true);
        setGameEnded(false);
        applyRoundInfo();
        break;
      case 'ended':
        setSongSelectionStarted(false);
        setIsPreloading(false);
        setGameStarted(false);
        setGameEnded(true);
        setScores(data.scores || data.players);
        break;
      case 'waiting':
      default:
        setSongSelectionStarted(false);
        setIsPreloading(false);
        setGameStarted(false);
        setGameEnded(false);
        break;
    }

    if (
      isDevMode &&
      !devBotsAddedRef.current &&
      me?.isCreator &&
      locationState?.devBots &&
      data.gameState === 'waiting'
    ) {
      socketService.devAddBots(data.roomId, locationState.devBots);
      setDevBotsAdded(true);
    }
  }, [isDevMode, locationState?.devBots]);

  // Socket setup effect
  useEffect(() => {
    // Only proceed if we have both a room ID and a username
    if (!roomId || !username) {
      return;
    }

    console.log('Initializing socket for room', roomId, 'as user', username);
    const subscriptions: socketService.Unsubscribe[] = [];

    // (Re)join whenever we have a live connection. The server recognises our player id, so this
    // is idempotent: a fresh join, a refresh and a reconnect all go through the same path.
    const join = () => {
      if (shouldCreateRoom) {
        socketService.createRoom(roomId, username);
      } else {
        socketService.joinRoom(roomId, username);
      }
    };

    subscriptions.push(socketService.onConnect(() => {
      console.log('Connected to socket with ID:', socketService.default.id);
      setReconnecting(false);
      setReconnectAttempt(0);
      join();
    }));
    if (socketService.isConnected()) {
      join();
    }

    subscriptions.push(socketService.onDisconnect((reason) => {
      console.log('Disconnected from socket:', reason);
      // Deliberate disconnects (navigating away / replaced session) are not "reconnecting".
      if (reason !== 'io client disconnect' && reason !== 'io server disconnect') {
        setReconnecting(true);
      }
    }));

    subscriptions.push(socketService.onReconnectAttempt((attempt) => {
      console.log(`Reconnection attempt ${attempt}`);
      setReconnecting(true);
      setReconnectAttempt(attempt);
    }));

    subscriptions.push(socketService.onReconnectFailed(() => {
      console.log('Failed to reconnect');
      toast.error(
        'Lost connection to the game server and failed to reconnect. Please refresh the page.',
        { duration: Infinity }
      );
    }));

    subscriptions.push(socketService.onSessionReplaced(() => {
      toast('You joined this room from another tab or device, so this one was disconnected.', { icon: 'ℹ️' });
      navigate('/', { replace: true });
    }));
    
    subscriptions.push(socketService.onRoomState((data) => {
      console.log('Room state received:', data);
      applyRoomState(data);
    }));
    
    subscriptions.push(socketService.onRoomNotFound(() => {
      console.log('Room not found');
      socketService.clearSessionInfo();
      navigate('/join', {
        replace: true,
        state: { error: `Room "${roomId}" no longer exists. It may have ended or been closed.` }
      });
    }));

    subscriptions.push(socketService.onJoinRejected(({ reason }) => {
      console.log('Join rejected on room page:', reason);
      const message = socketService.describeJoinRejection(reason);
      if (shouldCreateRoom) {
        toast.error(message);
        navigate('/', { replace: true });
        return;
      }
      navigate(socketService.getInvitePath(roomId), {
        replace: true,
        state: { error: message, username }
      });
    }));
    
    const applyPlayersUpdate = (data: socketService.PlayersUpdatePayload) => {
      if (!data || !data.players) return;
      setPlayers(data.players);
      const me = data.players.find((p: Player) => p.id === selfIdRef.current);
      setIsCreator(Boolean(me?.isCreator));
      if (data.gameState === 'ended') {
        setScores(data.scores || data.players);
      }
    };

    subscriptions.push(socketService.onPlayerJoined((data) => {
      console.log('Player joined event received:', data);
      applyPlayersUpdate(data);
    }));
    
    subscriptions.push(socketService.onPlayerLeft((data) => {
      console.log('Player left event received:', data);
      applyPlayersUpdate(data);
    }));

    subscriptions.push(socketService.onPlayerDisconnected((data) => {
      console.log('Player disconnected event received:', data);
      applyPlayersUpdate(data);
    }));
    
    subscriptions.push(socketService.onPlayerSelected((data) => {
      console.log(`Player ${data.username} selected a song`);
      setPlayersWhoSelected(prev => (prev.includes(data.playerId) ? prev : [...prev, data.playerId]));
    }));

    subscriptions.push(socketService.onSongDownloadStatus((data) => {
      // Only the selecting user should show a blocking spinner.
      if (data.username === username) {
        if (data.status === 'starting') setIsConfirmingSelection(true);
        if (data.status === 'success' || data.status === 'failed') setIsConfirmingSelection(false);
      }
    }));

    subscriptions.push(socketService.onSongSelectionFailed(({ message }) => {
      // Only the selecting user receives this event.
      setIsConfirmingSelection(false);
      setSelectedSong(null);
      setSelectionError(message || 'Something went wrong downloading that song. Please select another song.');
    }));
    
    subscriptions.push(socketService.onSongSelectionStarted((data) => {
      console.log('Song selection started event received');
      setSongSelectionStarted(true);
      setGameStarted(false);
      setPlayersWhoSelected([]);
      setGameEnded(false);
      setSelectedSong(null);
      setSelectionError('');
      setGameTheme(data?.theme || '');
    }));
    
    subscriptions.push(socketService.onSongsPreloading(({ songs }) => {
      console.log(`Songs preloading event received for ${songs.length} songs`);
      setIsPreloading(true);
      setTotalSongs(songs.length);
      setPreloadedCount(0);
    }));
    
    subscriptions.push(socketService.onPreloadProgress(({ preloaded, total }) => {
      setPreloadedCount(preloaded);
      setTotalSongs(total);
    }));
    
    subscriptions.push(socketService.onGameStarted(({ songs }) => {
      console.log(`Game started event received with ${songs.length} songs`);
      setGameStarted(true);
      setIsPreloading(false);
      setGameEnded(false);
      setTotalRounds(songs.length);
      setPlayersWhoSelected([]);
      setRevealedSong(null);
      setGuesses([]);
      setCloseGuesses([]);
    }));
    
    subscriptions.push(socketService.onRoundStarted(({ round, totalRounds, songUrl, songTitle, roundStartTime, songSelector }) => {
      console.log(`Round started: ${round}/${totalRounds}, songUrl: ${songUrl}`);
      setGameStarted(true);
      setIsPreloading(false);
      setCurrentRound(round);
      setTotalRounds(totalRounds);
      setCurrentSongUrl(songUrl);
      setCurrentSongTitle(songTitle);
      setRoundStartTime(roundStartTime);
      setGuesses([]);
      setCloseGuesses([]);
      setHasGuessedCorrectly(false);
      setRevealedSong(null);
      
      // Reset theme vote states
      setThemeVotes(0);
      setRequiredThemeVotes(0);
      setHasVotedInvalidTheme(false);
      setThemeVotePassed(false);
      
      setCurrentSongSelector(songSelector);
      setIsCurrentSongSelector(Boolean(songSelector && songSelector.id === selfIdRef.current));
    }));
    
    subscriptions.push(socketService.onRoundEnded(({ song }) => {
      setRevealedSong(song);
    }));
    
    subscriptions.push(socketService.onGameEnded(({ scores }) => {
      setScores(scores);
      
      // Delay the Game Over popup so players can see the last revealed song
      setTimeout(() => {
        setSongSelectionStarted(false);
        setIsPreloading(false);
        setGameStarted(false);
        setGameEnded(true);
      }, 5000);
    }));
    
    subscriptions.push(socketService.onNewGuess((guess) => {
      setGuesses((prevGuesses) => [...prevGuesses, guess]);
    }));
    
    subscriptions.push(socketService.onCloseGuess((guessData) => {
      if (guessData.playerId === selfIdRef.current) {
        setCloseGuesses((prevCloseGuesses) => [...prevCloseGuesses, { ...guessData, isClose: true }]);
      }
    }));
    
    subscriptions.push(socketService.onCorrectGuess((guessData) => {
      if (guessData.playerId === selfIdRef.current) {
        setHasGuessedCorrectly(true);
      }
      
      const { playerId, score, totalScore } = guessData as Guess & { score: number, totalScore: number };
      if (playerId && score !== undefined) {
        setPlayers(prevPlayers => 
          prevPlayers.map(player => 
            player.id === playerId 
              ? { ...player, score: totalScore !== undefined ? totalScore : player.score + score }
              : player
          )
        );
      }
      
      setGuesses((prevGuesses) => [
        ...prevGuesses.filter((g) => g.playerId !== guessData.playerId || g.guess !== guessData.guess),
        { ...guessData, isCorrect: true }
      ]);
    }));
    
    subscriptions.push(socketService.onSongSelectorRewarded((rewardData) => {
      const { playerId, totalScore } = rewardData;
      setPlayers(prevPlayers => 
        prevPlayers.map(player => 
          player.id === playerId 
            ? { ...player, score: totalScore }
            : player
        )
      );
      setGuesses(prevGuesses => [
        ...prevGuesses,
        {
          playerId,
          username: rewardData.username,
          guess: `+50 points for selecting this song!`,
          isReward: true
        }
      ]);
    }));
    
    subscriptions.push(socketService.onThemeVoteSubmitted((voteData) => {
      setThemeVotes(voteData.currentVotes);
      setRequiredThemeVotes(voteData.requiredVotes);
      if (voteData.playerId === selfIdRef.current) {
        setHasVotedInvalidTheme(true);
      }
      setGuesses(prevGuesses => [
        ...prevGuesses,
        {
          playerId: voteData.playerId,
          username: voteData.username,
          guess: `voted that this song doesn't fit the theme`,
          isVote: true,
          gameTheme: gameThemeRef.current
        }
      ]);
    }));
    
    subscriptions.push(socketService.onThemeVotePassed((voteData) => {
      setThemeVotePassed(true);
      setGuesses(prevGuesses => [
        ...prevGuesses,
        {
          playerId: '',
          username: 'System',
          guess: `Vote passed: ${voteData.username} will not receive points for this song.${
            voteData.pointsReverted > 0 ? ` ${voteData.pointsReverted} points have been reverted.` : ''
          }`,
          isSystemMessage: true
        }
      ]);
      if (voteData.pointsReverted > 0) {
        setPlayers(prevPlayers => 
          prevPlayers.map(player => 
            player.id === voteData.playerId 
              ? { ...player, score: player.score - voteData.pointsReverted }
              : player
          )
        );
      }
    }));
    
    subscriptions.push(socketService.onError(({ message, details }) => {
      console.error(`Socket error: ${message}${details ? ` - ${details}` : ''}`);
    }));
    
    socketService.connect();
    
    return () => {
      console.log('Cleaning up Room component and removing event listeners');
      subscriptions.forEach((unsubscribe) => unsubscribe());
      
      // Only disconnect if navigating away from the room entirely
      if (window.location.pathname !== `/room/${roomId}`) {
        socketService.default.disconnect();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, username, shouldCreateRoom, navigate, applyRoomState]);
  
  // Add a reconnection status indicator to the UI
  const renderReconnectionStatus = () => {
    if (reconnecting) {
      return (
        <div className="reconnect-banner" role="status" aria-live="polite">
          <span className="spinner spinner--sm" />
          <span>
            Reconnecting to the game…
            {reconnectAttempt > 0 && ` (attempt ${reconnectAttempt})`}
          </span>
        </div>
      );
    }

    if (!hasJoined) {
      return (
        <div className="reconnect-banner reconnect-banner--neutral" role="status" aria-live="polite">
          <span className="spinner spinner--sm" />
          <span>Joining room…</span>
        </div>
      );
    }

    return null;
  };

  const handleStartGame = () => {
    if (roomId) {
      socketService.startGame(roomId, theme);
    }
  };
  
  const describeSelectionFailure = (res: Exclude<socketService.SelectSongAck, { ok: true }>): string => {
    switch (res.code) {
      case 'VIDEO_TOO_LONG':
        return 'That video is too long. Please pick a video that is 10 minutes or shorter.';
      case 'START_TIME_OUT_OF_RANGE':
        return 'The start time you chose is past the end of that video. Pick an earlier start time.';
      default:
        return res.details ? `${res.error}: ${res.details}` : res.error;
    }
  };

  const handleSongSelect = (song: Song) => {
    setSelectedSong(song);
    setIsConfirmingSelection(true);
    setSelectionError('');
    
    if (roomId) {
      socketService.selectSong(roomId, song).then((res) => {
        if (res.ok) return;
        setIsConfirmingSelection(false);
        setSelectedSong(null);

        if (res.code) {
          // Validation / download failures are shown inline in the picker. `songSelectionFailed`
          // may already have set a message for download-phase failures; keep the first one.
          const message = describeSelectionFailure(res);
          setSelectionError(prev => prev || message);
        } else {
          // Transient rejections (e.g. "another download in progress") are just a toast.
          toast.error(res.details ? `${res.error}: ${res.details}` : res.error);
        }
      });
    }
  };

  const handleLeave = () => {
    socketService.clearSessionInfo();
    navigate('/');
  };
  
  const handleGuessSubmit = (guess: string) => {
    if (roomId) {
      socketService.submitGuess(roomId, guess);
    }
  };
  
  const handleInvalidThemeVote = () => {
    if (roomId) {
      socketService.voteInvalidTheme(roomId);
    }
  };

  const inviteUrl = roomId ? socketService.getInviteUrl(roomId) : '';

  const handleCopyRoomId = async () => {
    if (!roomId) return;
    const copied = await copyPlainText(roomId);
    if (copied) {
      toast.success('Room code copied');
    } else {
      toast.error('Could not copy the room code');
    }
  };

  const handleCopyInviteLink = async () => {
    if (!inviteUrl) return;
    const copied = await copyPlainText(inviteUrl);
    if (copied) {
      toast.success('Invite link copied');
    } else {
      toast.error('Could not copy the invite link');
    }
  };

  const stage: 'waiting' | 'song_selection' | 'preloading' | 'playing' | 'ended' = gameEnded
    ? 'ended'
    : gameStarted
      ? 'playing'
      : isPreloading
        ? 'preloading'
        : songSelectionStarted
          ? 'song_selection'
          : 'waiting';

  const stageLabel: Record<typeof stage, string> = {
    waiting: 'Lobby',
    song_selection: 'Picking songs',
    preloading: 'Loading',
    playing: `Round ${currentRound} of ${totalRounds}`,
    ended: 'Game over'
  };

  const humanPlayers = players.filter((p) => !p.isBot);
  const selectedCount = playersWhoSelected.filter((id) => humanPlayers.some((p) => p.id === id)).length;
  const sortedScores = [...scores].sort((a, b) => b.score - a.score);
  const topScore = sortedScores[0]?.score ?? 0;
  const isTiedForFirst = sortedScores.length > 1 && sortedScores[1].score === topScore;
  const winner = topScore > 0 && !isTiedForFirst ? sortedScores[0] : null;
  const CopyIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 15V6a2 2 0 0 1 2-2h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
  const LinkIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10 5.93" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.41a5 5 0 0 0 7.07 7.07L14 18.07" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );

  return (
    <div className="room">
      {renderReconnectionStatus()}

      {isConfirmingSelection && (
        <div className="modal-overlay modal-overlay--light" aria-live="polite">
          <div className="download-modal">
            <span className="spinner" />
            <div>
              <strong>Downloading song…</strong>
              <p className="muted small">Trimming your clip so everyone hears the same 60 seconds.</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="room-topbar">
        <div className="room-code-chip">
          <span className="room-code-label">Room</span>
          <code className="room-code">{roomId}</code>
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-icon--sm"
            onClick={handleCopyRoomId}
            aria-label="Copy room code"
            title="Copy room code"
          >
            <CopyIcon />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-icon--sm"
            onClick={handleCopyInviteLink}
            aria-label="Copy invite link"
            title="Copy invite link"
          >
            <LinkIcon />
          </button>
        </div>

        <span className={`stage-pill stage-pill--${stage}`}>
          <span className="stage-dot" aria-hidden="true" />
          {stageLabel[stage]}
        </span>

        {gameTheme && (
          <span className="theme-pill" title="Game theme">
            <span aria-hidden="true">✦</span> {gameTheme}
          </span>
        )}
      </div>
      
      <div className={`game-layout${gameStarted ? ' game-layout--playing' : ''}`}>
        <aside className="scores-sidebar panel">
          <PlayerList 
            players={players} 
            playersWhoSelected={playersWhoSelected}
            gameState={stage}
          />
        </aside>
        
        <section className="game-container">
          <AnimatePresence mode="wait">
            {!songSelectionStarted && !gameEnded ? (
              <motion.div 
                key="waiting-room"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                className="panel stage-card waiting-room" 
              >
                <div className="stage-icon" aria-hidden="true">
                  <span className="equalizer equalizer--lg">
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                  </span>
                </div>
                <h2>Waiting for players</h2>
                <p className="muted">
                  {players.length === 1
                    ? 'You’re the only one here so far. Share an invite link so friends can join with just a name.'
                    : `${players.length} players in the lobby. Share an invite link to bring in more.`}
                </p>

                <div className="invite-box">
                  <div className="invite-code">
                    <span className="invite-code-label">Invite link</span>
                    <code className="invite-url">{inviteUrl.replace(/^https?:\/\//, '')}</code>
                  </div>
                  <div className="invite-actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopyInviteLink}>
                      <LinkIcon /> Copy invite link
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={handleCopyRoomId}>
                      <CopyIcon /> Copy code
                    </button>
                  </div>
                </div>
                
                {isCreator ? (
                  <div className="host-controls">
                    <ThemePicker inputId="theme" label="Theme" value={theme} onChange={setTheme} />
                    <button 
                      type="button"
                      className="btn btn-primary btn-lg btn-block"
                      onClick={handleStartGame}
                      disabled={players.length < 2}
                    >
                      Start game
                    </button>
                    {players.length < 2 && (
                      <p className="muted small">You need at least 2 players to start.</p>
                    )}
                  </div>
                ) : (
                  <p className="muted small waiting-note">
                    The host will start the game once everyone’s in.
                  </p>
                )}
              </motion.div>
            ) : isPreloading ? (
              <motion.div 
                key="preloading"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                className="panel stage-card" 
              >
                <PreloadingScreen count={preloadedCount} total={totalSongs} />
              </motion.div>
            ) : !gameStarted ? (
              <motion.div 
                key="song-selection"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                className="panel song-selection" 
              >
                <div className="section-head">
                  <div>
                    <span className="eyebrow">Step 1</span>
                    <h2>Pick your song</h2>
                    <p className="muted">
                      {gameTheme
                        ? <>Choose something that fits <strong>{gameTheme}</strong>. Other players will try to guess it.</>
                        : 'Other players will try to guess it, so choose something recognisable.'}
                    </p>
                  </div>
                  {humanPlayers.length > 0 && (
                    <span className="count-pill">
                      {selectedCount}/{humanPlayers.length} ready
                    </span>
                  )}
                </div>

                {selectedSong ? (
                  <motion.div 
                    initial={{ scale: 0.96, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="selected-song"
                  >
                    <div className="selected-song-card">
                      <img src={selectedSong.thumbnail} alt="" />
                      <div className="selected-song-info">
                        <span className="status-pill status-pill--ready">Locked in</span>
                        <h3>{selectedSong.title}</h3>
                        <p className="muted">{selectedSong.channelTitle}</p>
                        {selectedSong.startTime && (
                          <p className="muted small">Starts at {selectedSong.startTime}</p>
                        )}
                      </div>
                    </div>
                    <div className="selected-song-wait">
                      <span className="spinner spinner--sm" />
                      Waiting for the other players to pick…
                    </div>
                  </motion.div>
                ) : (
                  <SongSearch
                    onSongSelect={handleSongSelect}
                    externalError={selectionError}
                    onClearExternalError={() => setSelectionError('')}
                  />
                )}
              </motion.div>
            ) : (
              <motion.div 
                key="game-play"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="game-play"
              >
                <div className="panel game-header">
                  <div className="round-row">
                    <span className="round-pill">
                      Round <strong>{currentRound}</strong> <span className="muted">/ {totalRounds}</span>
                    </span>
                    {currentSongSelector && (
                      <span className="picker-pill" title="Who picked this song">
                        <span aria-hidden="true">♪</span>
                        {isCurrentSongSelector ? 'Your pick' : `${currentSongSelector.username}’s pick`}
                      </span>
                    )}
                  </div>

                  {revealedSong ? (
                    <motion.div 
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', bounce: 0.45 }}
                      className="revealed-song"
                    >
                      <span className="revealed-label">The song was</span>
                      <h3 className="revealed-title text-gradient">{revealedSong.title}</h3>
                      {revealedSong.channelTitle && (
                        <p className="muted small">{revealedSong.channelTitle}</p>
                      )}
                    </motion.div>
                  ) : (
                    currentSongTitle && (
                      <BlankSongTitle 
                        title={currentSongTitle} 
                        roundStartTime={roundStartTime} 
                      />
                    )
                  )}
                </div>
                
                {/* Audio player section */}
                <div className="panel audio-section">
                  {currentSongUrl ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <SongPlayer
                        key={`${currentRound}-${currentSongUrl}`}
                        videoId={currentSongUrl}
                        roomId={roomId || ''} 
                        preloaded={true} 
                        roundStartTime={roundStartTime}
                      />
                    </motion.div>
                  ) : (
                    <div className="audio-loading">
                      <span className="spinner" />
                      <span>Waiting for the song to start…</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
        
        {gameStarted && (
          <aside className="chat-sidebar">
            <div className="panel chat-panel">
              <GuessChat 
                guesses={guesses} 
                onGuessSubmit={handleGuessSubmit} 
                disabled={hasGuessedCorrectly || !!revealedSong || isCurrentSongSelector}
                currentPlayerId={selfId}
                currentPlayerName={currentSongSelector?.username || ''}
                closeGuesses={closeGuesses}
                themeVotePassed={themeVotePassed}
              />
            </div>

            {isCurrentSongSelector && (
              <div className="status-card status-card--info">
                <span className="status-card-icon" aria-hidden="true">♪</span>
                <div>
                  <strong>This is your song.</strong>
                  <p>You sit this round out and earn +50 for every correct guess.</p>
                </div>
              </div>
            )}

            {!isCurrentSongSelector && gameTheme && (
              themeVotePassed ? (
                <div className="status-card status-card--warning">
                  <span className="status-card-icon" aria-hidden="true">⚑</span>
                  <div>
                    <strong>Vote passed: off-theme.</strong>
                    <p>
                      This song doesn’t fit “{gameTheme}”.
                      {currentSongSelector && ` ${currentSongSelector.username} won’t score this round.`}
                    </p>
                  </div>
                </div>
              ) : hasVotedInvalidTheme ? (
                <div className="status-card status-card--warning">
                  <span className="status-card-icon" aria-hidden="true">✓</span>
                  <div>
                    <strong>You flagged this song.</strong>
                    <p>
                      {themeVotes} of {requiredThemeVotes} votes needed to remove the picker’s bonus.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="status-card">
                  <div className="status-card-body">
                    <strong>Doesn’t fit “{gameTheme}”?</strong>
                    <p>Flag it. If enough players agree, the picker loses their bonus.</p>
                  </div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleInvalidThemeVote}>
                    Vote off-theme
                  </button>
                </div>
              )
            )}
          </aside>
        )}
      </div>
      
      {gameEnded && (
        <div className="modal-overlay">
          <motion.div
            className="modal results-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="results-title"
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', bounce: 0.3, duration: 0.5 }}
          >
            <div className="results-head">
              <span className="eyebrow">Game over</span>
              <h2 id="results-title">
                {winner ? (
                  <>
                    <span className="text-gradient">{winner.username}</span> wins
                  </>
                ) : isTiedForFirst && topScore > 0 ? (
                  'It’s a tie!'
                ) : (
                  'Final scores'
                )}
              </h2>
            </div>

            <ol className="results-list">
              {sortedScores.map((player, index) => (
                <li key={player.id} className={`results-row results-row--${Math.min(index + 1, 4)}`}>
                  <span className="results-rank">{index + 1}</span>
                  <span
                    className="avatar"
                    style={{ ['--hue' as string]: getAvatarHue(player.username) }}
                    aria-hidden="true"
                  >
                    {getInitial(player.username)}
                  </span>
                  <span className="results-name">{player.username}</span>
                  <span className="results-score">{player.score}</span>
                </li>
              ))}
            </ol>
            
            {isCreator ? (
              <div className="results-actions">
                <ThemePicker inputId="newTheme" label="Theme for the next game" value={theme} onChange={setTheme} variant="popover" />
                <div className="cta-row">
                  <button type="button" className="btn btn-primary btn-lg" onClick={handleStartGame}>
                    Play again
                  </button>
                  <button type="button" className="btn btn-secondary btn-lg" onClick={handleLeave}>
                    Leave
                  </button>
                </div>
              </div>
            ) : (
              <div className="results-wait">
                <span className="spinner spinner--sm" />
                Waiting for the host to start another game…
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Room;
